import { useEffect, useState } from 'react';
import { AlertTriangle, Check, Languages, Loader2, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { apiFetch, inputCls } from '../../utils/operations';

const LANGS = [
  { id: 'pt', label: 'PT' },
  { id: 'en', label: 'EN' },
  { id: 'fr', label: 'FR' },
  { id: 'de', label: 'DE' },
];

const STATUS_STYLE = {
  missing: 'bg-eco-ink/5 text-eco-ink/45',
  generated: 'bg-amber-100 text-amber-800',
  edited: 'bg-blue-100 text-blue-800',
  approved: 'bg-eco-green/10 text-eco-green',
};

const STATUS_LABEL = {
  missing: 'Em falta',
  generated: 'Gerada (rascunho)',
  edited: 'Editada manualmente',
  approved: 'Aprovada (pública)',
};

const SCALAR_LABELS = {
  name: 'Nome', overview: 'Overview', idealFor: 'Ideal para', style: 'Estilo',
  duration: 'Duração (texto)', accommodationNote: 'Nota de alojamento',
  transfersNote: 'Nota de transfers', meals: 'Refeições',
};
const LIST_LABELS = {
  highlights: 'Highlights', experiencesIncluded: 'Experiências incluídas', included: 'Incluído',
  notIncluded: 'Não incluído', optional: 'Opcional', important: 'Importante',
};

const toLines = (v) => (Array.isArray(v) ? v.join('\n') : v || '');
const fromLines = (v) => v.split('\n').map((s) => s.trim()).filter(Boolean);

/**
 * Per-language translation manager for a Full Package (PT is the source of truth).
 * Generated translations are drafts; nothing goes public without manual approval.
 * Packages carry no pricing fields — nothing commercial can enter a translation.
 */
export const AdminPackageTranslations = ({ pkgId, source, translations, onChange }) => {
  const [lang, setLang] = useState('en');
  const [edit, setEdit] = useState(null);
  const [busy, setBusy] = useState(false);

  const entry = translations?.[lang];
  const status = entry?.status || 'missing';

  useEffect(() => { setEdit(entry?.fields ? JSON.parse(JSON.stringify(entry.fields)) : null); }, [lang, entry]);

  const generate = async (confirmOverwrite = false) => {
    setBusy(true);
    try {
      const res = await apiFetch(`/admin/packages/${pkgId}/translations/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ langs: [lang], confirm_overwrite: confirmOverwrite }),
      });
      if (res.results?.[lang] === 'confirm_required') {
        if (window.confirm('Esta tradução foi editada/aprovada manualmente. Regenerar substitui o conteúdo atual. Continuar?')) {
          return generate(true);
        }
        return;
      }
      if (res.results?.[lang] === 'generated') toast.success(`${lang.toUpperCase()}: tradução gerada (rascunho)`);
      else toast.error(`${lang.toUpperCase()}: falhou — tenta novamente`);
      onChange(res.translations);
    } catch (e) {
      toast.error('Geração falhou', { description: String(e.message) });
    } finally {
      setBusy(false);
    }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      const updated = await apiFetch(`/admin/packages/${pkgId}/translations/${lang}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fields: edit }),
      });
      toast.success(`${lang.toUpperCase()}: tradução guardada (editada)`);
      onChange({ ...(translations || {}), [lang]: updated });
    } catch (e) {
      toast.error('Não foi possível guardar', { description: String(e.message) });
    } finally {
      setBusy(false);
    }
  };

  const approve = async () => {
    setBusy(true);
    try {
      const updated = await apiFetch(`/admin/packages/${pkgId}/translations/${lang}/approve`, { method: 'POST' });
      toast.success(`${lang.toUpperCase()}: tradução aprovada — fica pública`);
      onChange({ ...(translations || {}), [lang]: updated });
    } catch (e) {
      toast.error('Não foi possível aprovar', { description: String(e.message) });
    } finally {
      setBusy(false);
    }
  };

  const setField = (key, value) => setEdit((cur) => ({ ...cur, [key]: value }));

  const sourceSections = [
    ...Object.entries(SCALAR_LABELS).filter(([key]) => source?.[key]).map(([key, label]) => ({ key, label, value: source[key] })),
    ...Object.entries(LIST_LABELS).filter(([key]) => source?.[key]?.length).map(([key, label]) => ({ key, label, value: toLines(source[key]) })),
  ];

  return (
    <section className="rounded-xl border border-eco-ink/10 bg-eco-cream/50 p-5" data-testid="package-translations">
      <h3 className="flex items-center gap-2 text-base font-semibold text-eco-ink"><Languages size={16} className="text-eco-gold" /> Idiomas do conteúdo</h3>
      <p className="mt-1 text-xs text-eco-ink/50">O português é o conteúdo-base. Gera EN/FR/DE como rascunho, revê/edita e aprova — só traduções aprovadas ficam públicas. Guarda o package antes de gerar traduções.</p>

      <div className="mt-4 flex flex-wrap items-center gap-2" data-testid="package-translation-lang-tabs">
        {LANGS.map((l) => {
          const st = l.id === 'pt' ? 'source' : (translations?.[l.id]?.status || 'missing');
          const outdated = translations?.[l.id]?.outdated;
          return (
            <button
              key={l.id} type="button" data-testid={`package-translation-tab-${l.id}`} onClick={() => setLang(l.id)}
              className={`flex min-h-10 items-center gap-2 rounded-full border px-4 text-xs font-semibold transition-colors ${lang === l.id ? 'border-eco-green bg-eco-green text-white' : 'border-eco-ink/15 bg-white text-eco-ink/65 hover:border-eco-gold/50'}`}
            >
              {l.label}
              {l.id === 'pt' ? (
                <span className="rounded-full bg-white/20 px-2 py-0.5 text-[10px]">Fonte</span>
              ) : (
                <span className={`rounded-full px-2 py-0.5 text-[10px] ${STATUS_STYLE[st]}`}>{STATUS_LABEL[st]}{outdated ? ' · desatualizada' : ''}</span>
              )}
            </button>
          );
        })}
      </div>

      {lang === 'pt' && (
        <div className="mt-4 rounded-lg border border-eco-ink/10 bg-white p-4" data-testid="package-translation-source-pt">
          <p className="text-sm text-eco-ink/55">
            Conteúdo-base em português (somente leitura aqui). Edita-o no formulário acima e guarda antes de gerar; alterações posteriores marcam as traduções como desatualizadas sem as apagar.
          </p>
          <dl className="mt-4 grid gap-3 md:grid-cols-2">
            {sourceSections.map((item) => (
              <div key={item.key} className="rounded-lg bg-eco-cream/60 p-3" data-testid={`package-translation-source-${item.key}`}>
                <dt className="eco-field-label">{item.label}</dt>
                <dd className="mt-1 whitespace-pre-line text-xs leading-relaxed text-eco-ink/70">{item.value}</dd>
              </div>
            ))}
          </dl>
          {source?.itinerary?.length > 0 && (
            <div className="mt-3 rounded-lg bg-eco-cream/60 p-3" data-testid="package-translation-source-itinerary">
              <p className="eco-field-label">Itinerário</p>
              <ol className="mt-2 list-decimal space-y-1 pl-5 text-xs text-eco-ink/70">
                {source.itinerary.map((day, index) => <li key={index}><strong>{day.day}</strong> — {day.text}</li>)}
              </ol>
            </div>
          )}
        </div>
      )}

      {lang !== 'pt' && (
        <div className="mt-4 space-y-4">
          {entry?.outdated && (
            <p className="flex items-start gap-2 rounded-lg border border-amber-300 bg-amber-50 p-3 text-xs text-amber-800" data-testid="package-translation-outdated-warning">
              <AlertTriangle size={14} className="mt-0.5 shrink-0" /> O conteúdo original foi alterado. Esta tradução pode estar desatualizada.
            </p>
          )}

          <div className="flex flex-wrap gap-2">
            <button type="button" data-testid={`package-translation-generate-${lang}`} onClick={() => generate(false)} disabled={busy} className="eco-btn-primary !min-h-10 px-4 text-xs disabled:opacity-60">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} {status === 'missing' ? 'Gerar tradução' : 'Regenerar tradução'}
            </button>
            {edit && <button type="button" data-testid={`package-translation-save-${lang}`} onClick={saveEdit} disabled={busy} className="eco-btn-secondary !min-h-10 px-4 text-xs">Guardar edição</button>}
            {edit && (status !== 'approved' || entry?.outdated) && <button type="button" data-testid={`package-translation-approve-${lang}`} onClick={approve} disabled={busy} className="eco-btn-secondary !min-h-10 px-4 text-xs !border-eco-green/40 !text-eco-green"><Check size={14} /> {entry?.outdated ? 'Reaprovar tradução' : 'Aprovar e publicar'}</button>}
          </div>

          {!edit && status === 'missing' && (
            <p className="text-sm text-eco-ink/50" data-testid={`package-translation-empty-${lang}`}>Sem tradução {lang.toUpperCase()} ainda. Gera a partir do conteúdo PT.</p>
          )}

          {edit && (
            <div className="space-y-4" data-testid={`package-translation-editor-${lang}`}>
              {Object.entries(SCALAR_LABELS).map(([key, label]) => edit[key] !== undefined && (
                <label key={key} className="block">
                  <span className="eco-field-label mb-1 block">{label}</span>
                  {key === 'overview'
                    ? <textarea rows={4} data-testid={`pkg-tr-edit-${key}`} value={edit[key]} onChange={(e) => setField(key, e.target.value)} className={inputCls} />
                    : <input data-testid={`pkg-tr-edit-${key}`} value={edit[key]} onChange={(e) => setField(key, e.target.value)} className={inputCls} />}
                </label>
              ))}
              {Object.entries(LIST_LABELS).map(([key, label]) => edit[key] !== undefined && (
                <label key={key} className="block">
                  <span className="eco-field-label mb-1 block">{label} <span className="font-normal text-eco-ink/40">(um por linha)</span></span>
                  <textarea rows={3} data-testid={`pkg-tr-edit-${key}`} value={toLines(edit[key])} onChange={(e) => setField(key, fromLines(e.target.value))} className={inputCls} />
                </label>
              ))}
              {edit.itinerary && (
                <fieldset className="rounded-lg border border-eco-ink/10 bg-white p-4" data-testid="pkg-tr-edit-itinerary">
                  <legend className="px-1 text-xs font-semibold text-eco-ink/55">Itinerário traduzido</legend>
                  <div className="space-y-3">
                    {(edit.itinerary || []).map((day, index) => (
                      <div key={index} className="rounded-lg bg-eco-cream/50 p-3" data-testid={`pkg-tr-edit-itinerary-day-${index + 1}`}>
                        <p className="mb-2 text-xs font-bold text-eco-ink/50">Dia {index + 1}</p>
                        {['day', 'text'].filter((key) => day[key] !== undefined).map((key) => (
                          <label key={key} className="mb-3 block last:mb-0">
                            <span className="eco-field-label mb-1 block">{key === 'day' ? 'Rótulo do dia' : 'Texto'}</span>
                            {String(day[key]).length > 90
                              ? <textarea rows={3} data-testid={`pkg-tr-edit-itinerary-${index + 1}-${key}`} value={day[key]} onChange={(e) => setField('itinerary', edit.itinerary.map((item, i) => i === index ? { ...item, [key]: e.target.value } : item))} className={inputCls} />
                              : <input data-testid={`pkg-tr-edit-itinerary-${index + 1}-${key}`} value={day[key]} onChange={(e) => setField('itinerary', edit.itinerary.map((item, i) => i === index ? { ...item, [key]: e.target.value } : item))} className={inputCls} />}
                          </label>
                        ))}
                      </div>
                    ))}
                  </div>
                </fieldset>
              )}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
